import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Swal from "sweetalert2";
import api from "../../../utils/axios";
import CustomerTabs from "../../components/CustomerTabs";
import ContractTable from "../../components/Contract/ContractTable";
import ContractSignModal from "../../components/Modal/Contract/ContractSignModal";

export default function ContractListPage() {
  const { id: AR_Id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchedContractNo = queryParams.get("search") || "";
  const initialPage = Math.max(1, Number(queryParams.get("page")) || 1);
  const initialLimit = Math.max(1, Number(queryParams.get("limit")) || 100);
  const initialFocusedId = queryParams.get("focus") || null;
  const backLink = location.state?.backLink || null;
  const backLabel = location.state?.backLabel || "ใบเสนอราคา";

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [search, setSearch] = useState(searchedContractNo);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [focusedContractId, setFocusedContractId] = useState(initialFocusedId);

  // ====================== โหลดข้อมูล ======================
  const fetchContracts = async (keyword = "", isInitial = false) => {
    try {
      if (isInitial) setLoading(true);

      const params = new URLSearchParams();
      const role = String(localStorage.getItem("role") || "").trim();
      const empId = String(localStorage.getItem("emp_id") || "").trim();
      const isAdmin = role === "Admin";
      if (AR_Id) params.set("AR_Id", AR_Id);
      if (keyword) params.set("keyword", keyword);
      if (!isAdmin && empId) params.set("emp_id", empId);
      const url = `/get-contracts?${params}`;
      const res = await api.get(url);

      const mapped = (res.data?.result || []).map((c) => {
        const addressParts = [
          c.AddrNo && `เลขที่ ${c.AddrNo}`,
          c.Village && `หมู่บ้าน ${c.Village}`,
          c.Moo && `หมู่ ${c.Moo}`,
          c.Soi && `ซ.${c.Soi}`,
          c.Road && `ถ.${c.Road}`,
          c.SubDistrictName && `ต.${c.SubDistrictName}`,
          c.DistrictName && `อ.${c.DistrictName}`,
          c.ProvinceName && `จ.${c.ProvinceName}`,
          c.Zipcode,
        ]
          .filter(Boolean)
          .join(" ");

        return {
          Contract_Id: c.Doc_Id,
          Contract_No: c.Doc_No,
          Contract_Date: c.Doc_Date,
          Quote_No: c.Ref_DocNo || "-",
          Service_Name: c.Service_Name || "-",
          Sale_Emp_Id: c.Sale_Emp_Id ?? null,
          AR_Id: c.AR_Id, // เพิ่ม AR_Id เพื่อใช้เป็นลิงก์
          AR_Name: c.AR_Name || "-",
          AR_LastName: c.AR_LastName || "-",
          DisplayName: c.DisplayName || "-",
          ServiceAddr_Name: c.ServiceAddr_Name,
          Total_Amount: Number(c.NetTotal_Price) || 0, // จาก CT_H.NetTotal_Price (หรือคำนวณจากจุดย่อยเมื่อมี)
          Status: c.Doc_Status,
        };
      });

      const visibleContracts =
        !isAdmin && empId
          ? mapped.filter((contract) => String(contract.Sale_Emp_Id ?? "") === empId)
          : mapped;

      setContracts(visibleContracts);

      // โหลดข้อมูลลูกค้า เมื่อมี AR_Id (เฉพาะครั้งแรก)
      if (isInitial && AR_Id) {
        const custRes = await api.get(`/get-tb-set-ar1/${AR_Id}`);
        setCustomer(custRes.data?.result?.recordset?.[0] || null);
      }
    } catch (err) {
      console.error("❌ โหลดข้อมูลสัญญาไม่สำเร็จ:", err);
      Swal.fire("ผิดพลาด!", "โหลดข้อมูลสัญญาไม่สำเร็จ", "error");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts(searchedContractNo, true);
  }, [AR_Id]);

  const handleSearchChange = (value) => {
    setSearch(value);
    fetchContracts(value);
  };

  const buildContractListLink = (focusedId = null) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (currentPage > 1) params.set("page", String(currentPage));
    if (itemsPerPage !== 100) params.set("limit", String(itemsPerPage));
    if (focusedId != null) params.set("focus", String(focusedId));
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  };

  const getBreadcrumbState = (focusedId = null) => ({
    parentBackLink: backLink,
    parentBackLabel: backLabel,
    contractListLink: buildContractListLink(focusedId),
    contractListLabel: search ? `สัญญา ${search}` : "สัญญา",
  });

  // ====================== ลบ ======================
  const handleDelete = async (contract) => {
    Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบสัญญา ${contract.Contract_No}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/delete-contract/${contract.Contract_Id}`);
          fetchContracts(search);
        } catch (err) {
          Swal.fire("ผิดพลาด!", "ไม่สามารถลบสัญญาได้", "error");
        }
      }
    });
  };

  // ====================== แก้ไข ======================
  const handleEdit = (contract) => {
    const focusedId = contract?.Contract_Id ?? null;
    setFocusedContractId(focusedId);
    navigate(`/contract/edit/${focusedId}`, { state: getBreadcrumbState(focusedId) });
  };

  // ====================== ดูข้อมูล (view-only) ======================
  const handleView = (contract) => {
    const focusedId = contract?.Contract_Id ?? null;
    setFocusedContractId(focusedId);
    navigate(`/contract/view/${focusedId}`, { state: getBreadcrumbState(focusedId) });
  };

  // ====================== สร้าง ======================
  const handleCreate = () => {
    navigate(AR_Id ? `/contract/create/${AR_Id}` : `/contract/create`, {
      state: getBreadcrumbState(focusedContractId),
    });
  };

  // ====================== Assign Job ======================
  const handleAssignJob = (contract) => {
    const focusedId = contract?.Contract_Id ?? null;
    setFocusedContractId(focusedId);
    navigate(`/contract/edit/${focusedId}`, { state: getBreadcrumbState(focusedId) });
  };

  // ====================== เซ็น ======================
  const handleSign = async (contract) => {
    try {
      const res = await api.get(`/get-contract/${contract.Contract_Id}`);
      const contractData = res.data.header;

      setSelectedContract({
        ...contract,
        Sign_Password: contractData.Sign_Password || "ไม่พบรหัสผ่าน",
      });

      setSignModalOpen(true);
    } catch (err) {
      Swal.fire("ผิดพลาด!", "โหลดข้อมูลรหัสผ่านไม่สำเร็จ", "error");
    }
  };

  const handleSignedSuccess = (contractId, updatedStatus) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.Contract_Id === contractId ? { ...c, Status: updatedStatus } : c
      )
    );
    setSignModalOpen(false);
  };

  // ====================== RENDER ======================
  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      {/* ⭐ เหมือนหน้าใบเสนอราคา ⭐ */}
      <main
        className="flex-1 h-screen bg-gray-50 flex flex-col"
        style={{
          height: "100vh",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
        }}
      >
        <Topbar
          breadcrumbs={
            backLink
              ? [
                  { label: "ลูกค้า", link: "/sale" },
                  { label: "รายการลูกค้า", link: "/sale" },
                  { label: backLabel, link: backLink },
                  { label: searchedContractNo ? `สัญญา ${searchedContractNo}` : "สัญญา" },
                ]
              : [
                  { label: "ลูกค้า", link: "" },
                  { label: "รายการลูกค้า", link: "/sale" },
                  { label: "สัญญา" },
                ]
          }
        />

        {/* ⭐ ถ้ามีลูกค้า ให้โชว์ Tabs */}
        
        <div className="sticky top-14 z-20 bg-white">
          {AR_Id && <CustomerTabs customer={customer} />}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden pl-2 pr-8 pb-0 pt-16">
          {loading ? (
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          ) : (
            <ContractTable
              contracts={contracts}
              onEdit={handleEdit}
              onView={handleView}
              onDelete={handleDelete}
              onSign={handleSign}
              onCreate={handleCreate}
              onAssignJob={handleAssignJob}
              fillAvailable
              allowCreate={!AR_Id}
              search={search}
              onSearchChange={handleSearchChange}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              focusedContractId={focusedContractId}
              onFocusContract={(contract) => setFocusedContractId(contract?.Contract_Id ?? null)}
            />
          )}
        </div>

        <ContractSignModal
          isOpen={signModalOpen}
          onClose={() => setSignModalOpen(false)}
          contract={selectedContract}
          onSuccess={(status) =>
            handleSignedSuccess(selectedContract?.Contract_Id, status)
          }
        />
      </main>
    </div>
  );
}
